import { useSnackbar } from 'notistack';
import {
  ChangeEvent,
  Dispatch,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  getSiteTaskDetail,
  getSiteTaskList,
  updateSiteTask
} from '../../../../apicalls/task';
import { getSiteProcessNameList } from '../../../../apicalls/process';
import { TSiteProcessNameListData } from '../../../../types/process';
import { checkResData } from '../../../../utils/common';
import { TSelectItem } from '../../../../types/common';
import { TSiteTaskDetailData, TSiteTaskListData } from '../../../../types/task';
import { SITE_TASK_STATUS, SEARCH_STATUS } from '../../../../const/TaskConst';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';
import { SITE_TASK_DISPLAY_LIMIT } from '../../../../const/Const';

export type TProps = {
  isOpen: boolean;
  constructId: number;
  siteProcessId: number;
  includeChildSiteProcess: boolean;
  setIncludeChildSiteProcess: Dispatch<boolean>;
  viewAlert?: boolean;
};

export const useHooks = (props: TProps) => {
  const {
    isOpen,
    constructId,
    siteProcessId,
    includeChildSiteProcess,
    setIncludeChildSiteProcess,
    viewAlert
  } = props;

  // スクロール設定
  const [paginationState, setPaginationState] = useState({
    currentPage: 1, // 現在のページ
    hasMorePage: false // 残ページの有無
  });
  // 選択中の工事
  const [selectedConstructId, setSelectedConstructId] = useState(constructId);
  // 選択中の工程
  const [searchSiteProcessId, setSearchSiteProcessId] = useState(siteProcessId);
  // 選択中のステータス
  const [searchStatus, setSearchStatus] = useState(SEARCH_STATUS.ALL.value);
  useState(includeChildSiteProcess);
  // 工程選択リスト
  const [siteProcessList, setSiteProcessList] = useState<TSelectItem[]>([
    { value: 0, label: '工程' }
  ]);
  // タスク一覧データ
  const [siteTaskList, setSiteTaskList] = useState<Array<TSiteTaskListData>>(
    []
  );
  // 変更履歴表示関連
  const [isOpenSiteTaskHistory, setIsOpenSiteTaskHistory] = useState(false);
  // タスク追加モーダル表示用
  const [isOpenSiteTaskAdd, setIsOpenSiteTaskAdd] = useState(false);
  // タスク更新モーダル表示用
  const [isOpenSiteTaskEdit, setIsOpenSiteTaskEdit] = useState(false);
  // タスク削除モーダル表示用
  const [isOpenSiteTaskDelete, setIsOpenSiteTaskDelete] = useState(false);
  // 更新対象のタスク特定用
  const [editSiteTaskId, setEditSiteTaskId] = useState(0);
  // 削除対象のタスク特定用
  const [deleteSiteTaskId, setDeleteSiteTaskId] = useState(0);
  // 再レンダリングのトリガ用state
  const [renderSiteTaskDataTrigger, setRenderSiteTaskDataTrigger] =
    useState(false);
  // 再レンダリング
  const reRenderSiteTaskData = () => {
    setRenderSiteTaskDataTrigger(prev => !prev);
    setPaginationState(() => ({
      currentPage: 1,
      hasMorePage: true
    }));
    fetchTaskList(undefined, undefined, undefined, undefined, 1);
  };
  // ローディング用
  const [isLoading, setIsLoading] = useState(true);
  // ローディング用
  const [isSearching, setIsSearching] = useState(false);
  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // タスク一覧情報の取得
  // データ読み込み＆画面に描画するデータ作成処理
  const fetchTaskList = (
    currentSiteProcessId = searchSiteProcessId,
    currentStatus = searchStatus,
    currentIncludeChildSiteProcess = includeChildSiteProcess,
    currentConstructId = selectedConstructId,
    currentPage = paginationState.currentPage
  ) => {
    const taskStatus = getTaskStatusForAPI(currentStatus);
    const alertOnly = currentStatus === SEARCH_STATUS.ALERT_ONLY.value;

    setIsSearching(true);
    getSiteTaskList(
      currentConstructId,
      currentSiteProcessId,
      undefined,
      taskStatus,
      alertOnly,
      currentIncludeChildSiteProcess,
      currentPage,
      SITE_TASK_DISPLAY_LIMIT
    ).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          if (currentPage === 1) {
            setSiteTaskList(data.data);
          } else {
            setSiteTaskList(prev => [...prev, ...data.data]);
          }
          // スクロール設定を更新
          setPaginationState(prevState => {
            const hasMore = data.page_count > prevState.currentPage;
            if (prevState.hasMorePage !== hasMore) {
              return {
                ...prevState,
                hasMorePage: hasMore
              };
            }
            return prevState;
          });
        }
        // ローディング終了
        setIsLoading(false);
        setIsSearching(false);
      });
    });
  };

  // 工程セレクトボックス選択時処理
  const handleChangeSearchSiteProcess = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchSiteProcessId(parseInt(e.target.value));
    fetchSearchSiteTaskList(parseInt(e.target.value));
  };

  // ステータスセレクトボックス選択時処理
  const handleChangeSearchStatus = (e: ChangeEvent<HTMLSelectElement>) => {
    setSearchStatus(parseInt(e.target.value));
    fetchSearchSiteTaskList(searchSiteProcessId, parseInt(e.target.value));
  };

  // ステータス変更時の処理
  const handleChangeStatus = (
    e: ChangeEvent<HTMLSelectElement>,
    taskId: number
  ) => {
    const status = parseNumber(e.target.value) ?? 0;
    updateStatus(taskId, status);
  };

  // 小工程を含むタスクを表示するチェックボックス変更時の処理
  const handleChangeIncludeChildSiteProcess = (
    e: ChangeEvent<{}>,
    checked: boolean
  ) => {
    setIncludeChildSiteProcess(checked);
    fetchSearchSiteTaskList(searchSiteProcessId, searchStatus, checked);
  };

  // 検索条件変更時のタスクリスト取得処理
  const fetchSearchSiteTaskList = (
    currentProcessId = searchSiteProcessId,
    currentStatus = searchStatus,
    currentIncludeChildProcess = includeChildSiteProcess,
    currentConstructId = selectedConstructId
  ) => {
    if (isOpen) {
      const currentPagination = {
        currentPage: 1,
        hasMorePage: true
      };
      setPaginationState(() => currentPagination);

      fetchTaskList(
        currentProcessId,
        currentStatus,
        currentIncludeChildProcess,
        currentConstructId,
        currentPagination.currentPage
      );
    }
  };

  // タスクのステータスを更新する
  const updateStatus = (siteTaskId: number, status: number) => {
    getSiteTaskDetail(siteTaskId).then((resData: any) => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const siteTask: TSiteTaskDetailData = data.data;
          updateSiteTask(
            siteTask.id,
            siteTask.site_process.id,
            siteTask.site_task_name,
            status,
            siteTask.charge_user?.id,
            siteTask.planned_end_date,
            siteTask.detail,
            siteTask.order_category,
            siteTask.order?.id,
            siteTask.actual_end_date
          ).then((resData: any) => {
            checkResData(resData, enqueueSnackbar).then(() => {
              reRenderSiteTaskData();

              fetchSearchSiteTaskList(searchSiteProcessId);
            });
          });
        }
      });
    });
  };

  // タスク一覧取得API用タスクステータス取得処理
  const getTaskStatusForAPI = (searchStatus: number) => {
    switch (searchStatus) {
      // 「すべて」と「アラートのみ」の場合はステータス指定なし
      case SEARCH_STATUS.ALL.value:
      case SEARCH_STATUS.ALERT_ONLY.value:
        return undefined;
      // 「すべて」と「アラートのみ」以外は検索用ステータスとタスクステータスは一致
      case SEARCH_STATUS.NOT_STARTED.value:
        return SITE_TASK_STATUS.NOT_STARTED.value;
      case SEARCH_STATUS.IN_PROGRESS.value:
        return SITE_TASK_STATUS.IN_PROGRESS.value;
      case SEARCH_STATUS.COMPLETED.value:
        return SITE_TASK_STATUS.COMPLETED.value;
      case SEARCH_STATUS.UNNECESSARY.value:
        return SITE_TASK_STATUS.UNNECESSARY.value;
      default:
        return searchStatus;
    }
  };

  const observer = useRef<IntersectionObserver>();

  // スクロールが最下部に移動したとき次ページをセット
  const lastElementRef = useCallback((node: any) => {
    if (observer === undefined) {
      return;
    }
    if (observer.current) {
      observer.current.disconnect();
    }
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        // 現在のページを更新
        setPaginationState(prevState => ({
          ...prevState,
          currentPage: prevState.currentPage + 1
        }));
        observer.current?.disconnect();
      }
    });
    if (node) {
      observer.current.observe(node);
    }
  }, []);

  // 追加取得発火用
  useEffect(() => {
    if (
      isOpen &&
      paginationState.hasMorePage &&
      paginationState.currentPage > 1
    ) {
      fetchTaskList();
    }
  }, [paginationState]);

  // モーダルを開いた際の初期描画
  useEffect(() => {
    if (isOpen) {
      // ローディング開始
      setIsLoading(true);
      // 初期値設定
      setSelectedConstructId(constructId);
      setSearchSiteProcessId(siteProcessId);
      setSearchStatus(SEARCH_STATUS.ALL.value);

      // 工程一覧セレクトボックスの設定
      getSiteProcessNameList(constructId, false).then((resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            const listData: TSiteProcessNameListData[] = data.data;
            const list: TSelectItem[] = [];
            list.push({ value: 0, label: '工程' });
            listData.forEach((siteProcess: TSiteProcessNameListData) => {
              list.push({
                value: siteProcess.id,
                label:
                  siteProcess.parent_site_process_id === null
                    ? siteProcess.site_process_name
                    : '　' + siteProcess.site_process_name
              });
            });
            setSiteProcessList(list);
          }
        });
      });

      // アラートのみ表示の場合、検索ステータスをセット
      if (viewAlert === true) {
        setSearchStatus(SEARCH_STATUS.ALERT_ONLY.value);
        fetchSearchSiteTaskList(
          siteProcessId,
          SEARCH_STATUS.ALERT_ONLY.value,
          includeChildSiteProcess,
          constructId
        );
      } else {
        fetchSearchSiteTaskList(
          siteProcessId,
          SEARCH_STATUS.ALL.value,
          includeChildSiteProcess,
          constructId
        );
      }
    }
  }, [isOpen]);

  return {
    siteTaskList,
    siteProcessList,
    searchSiteProcessId,
    searchStatus,
    isLoading,
    isOpenSiteTaskAdd,
    isOpenSiteTaskEdit,
    isOpenSiteTaskDelete,
    isOpenSiteTaskHistory,
    editSiteTaskId,
    deleteSiteTaskId,
    setIsOpenSiteTaskAdd,
    setIsOpenSiteTaskEdit,
    setIsOpenSiteTaskDelete,
    setIsOpenSiteTaskHistory,
    setEditSiteTaskId,
    setDeleteSiteTaskId,
    handleChangeSearchSiteProcess,
    handleChangeSearchStatus,
    handleChangeIncludeChildSiteProcess,
    renderSiteTaskDataTrigger,
    reRenderSiteTaskData,
    handleChangeStatus,
    lastElementRef,
    isSearching
  };
};
